import React from 'react';
import { Users, CalendarClock } from 'lucide-react';
import { Input } from '../../../components/ui';
import { Label } from '../../../components/ui';
import { FieldError, useFieldInvalid } from '../../../components/entity/FieldError.js';
import type { WizardState } from './hire-types.js';

interface HireStepSupervisionProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

export const HireStepSupervision: React.FC<HireStepSupervisionProps> = ({ state, onChange }) => {
  const supervisorInvalid = useFieldInvalid('supervisorDaemonId');
  const probationInvalid = useFieldInvalid('probationDays');
  return (
    <div className="space-y-4">
      <div>
        <Label hint="optional">Supervisor Daemon ID</Label>
        <div className="flex items-center gap-2">
          <Users size={16} className="text-muted shrink-0" />
          <Input
            type="number"
            value={state.supervisorDaemonId ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ supervisorDaemonId: val ? Number(val) : null });
            }}
            placeholder="ID of the supervising daemon"
            invalid={supervisorInvalid}
          />
        </div>
        <FieldError name="supervisorDaemonId" />
        <p className="text-xs text-faint mt-1">
          A supervisor daemon reviews this daemon's findings before they are accepted. Leave empty for no supervisor.
        </p>
      </div>

      <div>
        <Label hint="optional">Probation Period</Label>
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-muted shrink-0" />
          <Input
            type="number"
            value={state.probationDays ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ probationDays: val ? Number(val) : null });
            }}
            placeholder="Number of days"
            min={0}
            invalid={probationInvalid}
          />
          <span className="text-sm text-muted shrink-0">days</span>
        </div>
        <FieldError name="probationDays" />
        <p className="text-xs text-faint mt-1">
          During probation, all actions require human approval regardless of other settings. Leave empty for no
          probation.
        </p>
      </div>

      <div className="p-4 bg-surface-alt rounded-lg border mt-2">
        <h4 className="text-sm font-medium text-heading mb-2">Supervision levels</h4>
        <ul className="space-y-2 text-xs text-muted">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 shrink-0" />
            <span>
              <strong className="text-body">No supervisor, no probation</strong> — daemon operates autonomously based on
              its permission tier
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-warning mt-1.5 shrink-0" />
            <span>
              <strong className="text-body">Supervisor only</strong> — another daemon reviews findings before acceptance
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-danger mt-1.5 shrink-0" />
            <span>
              <strong className="text-body">Probation active</strong> — all actions require human approval for the
              specified period
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};
